import { observer } from 'mobx-react';
import { flexRender, ColumnDef, Row, ColumnSort, RowData } from '@tanstack/react-table';
import { route } from '@monorepo/tools/src/lib/types/url';
import { InjectedTableActionsProps, IRowProps, ITableLiners, ITableProps, Table } from '@monorepo/base/src/components/table/table';
import { PrimaryLink } from '@monorepo/base/src/components/buttons/buttons';
import { FilterModel } from '../../models/filter.model';
import { memo, useEffect } from 'react';
import { IDebugProps } from '@monorepo/tools/src/lib/interfaces/debug';
import { DataAttribute, generateDataAttrs, suffixToDataAttr } from '@monorepo/tools/src/lib/models/data-attr.model';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';
import { snakeCase } from 'change-case';
import { PaginationState, VisibilityState } from '@tanstack/react-table';
import { IRequestFlags } from '../../stores/http.store';
import { IControlledStore, useStores } from '../../hooks/use-stores';
import { BetweenPagesStateProps } from '@monorepo/tools/src/lib/interfaces/global';
import React from 'react';

declare module '@tanstack/table-core' {
	// eslint-disable-next-line
	interface ColumnMeta<TData extends RowData, TValue> {
		isPerformance?: boolean;
		colSpan?: number;
		// TODO add total line here
	}
}

export interface IServerPagination {
	offset: number;
	limit: number;
}

interface IControlledTableProps<T> extends ITableProps<T> {
	// TODO - fix this any
	// https://github.com/TanStack/table/issues/4382
	columns: ColumnDef<T, unknown>[];
	data: Array<T>;
	children?(props: InjectedTableActionsProps<T>): ITableLiners;
	createRoute?: route;
	isLoading?: boolean;
	defaultSortBy?: ColumnSort[];
	debugProps?: IDebugProps;
	isError?: boolean;
	isActionLiner?: boolean;
	currentRoute: string;
	onPageChange?: (pagination: PaginationState) => void;
	fetchReports?: (flags?: IRequestFlags) => void;
	fetchMetrics?: (flags?: IRequestFlags) => void;
	isEmptyAndDisabled?: boolean;
	columnVisibility?: VisibilityState;
	statsFilters?: React.ReactNode;
	creativesLinkedToggle?: React.ReactNode;
	makeLinerPortable?: boolean;
	teleportRootId?: string;
	metricsKey?: string;
	skipFilter?: boolean;
}

function theFilter<T>(data: Array<T>, filters?: Map<number, FilterModel>, forceFilterBackend?: boolean, skipFilter?: boolean): Array<T> {
	if (!filters || forceFilterBackend) {
		return data;
	}

	let filteredData = data;
	if (skipFilter) {
		return filteredData;
	}
	Array.from(filters).forEach(([, filter]) => {
		const { prototype } = filter;
		const { filterFunc } = prototype;

		if (!filterFunc) {
			return filteredData;
		}

		filteredData = filterFunc(filteredData, filter);
	});

	return filteredData;
}

export const _ControlledTable = observer(<T extends IRowProps<T>>(props: IControlledTableProps<T>) => {
	const {
		columns,
		data,
		children,
		isLoading,
		createRoute,
		isSummary,
		debugProps,
		currentRoute,
		hideFiltersActions,
		blockRerenderOnFilterChange,
		onPageChange,
		totalRows,
		totalFilteredRows,
		fetchReports,
		fetchMetrics,
		isActionLiner = true,
		isEmptyAndDisabled,
		columnVisibility,
		isGlobalFilter,
		removeColumnsPicker,
		statsFilters,
		creativesLinkedToggle,
		makeLinerPortable,
		teleportRootId,
		skipFilter,
		...rest
	} = props;

	const { tooltipsStore } = useStores<IControlledStore>();
	const { defaultSortBy } = props;
	const { dataAttrs } = debugProps || {};
	const { tableStore } = usePageSettings(currentRoute);

	// const [pagination, setPagination] = useState<{ offset: number; limit?: number }>({ offset: 0, limit: undefined });

	let TableActions: ITableLiners = {};
	let performanceProps = {};
	const currentFilters = tableStore?.getFiltersStore(currentRoute)?.getCurrentFilters();
	const filterTerm = tableStore?.getFilterTerm();
	const firstFilterValue = currentFilters?.entries()?.next()?.value?.[1]?.value;
	const statusFilterLength =
		typeof firstFilterValue === 'string' || Array.isArray(firstFilterValue) ? firstFilterValue.length : firstFilterValue;

	const totalRowsInt = parseInt(`${totalRows}`.replace(',', ''));
	const filteredData =
		totalRowsInt > data.length ? data : theFilter(data, currentFilters, Boolean(fetchReports || fetchMetrics), skipFilter);

	useEffect(() => {
		if (blockRerenderOnFilterChange) {
			return;
		}
		tooltipsStore.removeTooltips();
		if (tableStore?.getPageIndex() !== 0) {
			tableStore?.setPageIndex(0);
		}
		if (fetchReports) {
			// && (totalRowsInt > data.length || !totalRowsInt)) {
			fetchReports({ appendData: false, resetOffset: true });
		}
		if (fetchMetrics) {
			fetchMetrics();
		}
	}, [JSON.stringify(Array.from((currentFilters || new Map()).values())), filterTerm, statusFilterLength, JSON.stringify(defaultSortBy)]);

	const renderWithPerformance = (row: Row<T>, { className }: { className: string }): JSX.Element[] => {
		return row.getVisibleCells().map(cell => {
			const columnHeader = cell.column.columnDef.header;
			const columnName = typeof columnHeader === 'string' ? [new DataAttribute('id', `${snakeCase(columnHeader)}_cell`)] : undefined;

			return (
				<td key={cell.id} className={className} colSpan={cell.column.columnDef.meta?.colSpan} {...generateDataAttrs(columnName)}>
					{flexRender(cell.column.columnDef.cell, cell.getContext())}
				</td>
			);
		});
	};

	if (isSummary) {
		performanceProps = {
			renderCell: renderWithPerformance,
			isBarLoader: isLoading,
			defaultSortBy: defaultSortBy || [{ id: 'wins', desc: true }],
		};
	}

	if (!tableStore) {
		return null;
	}
	return (
		<Table<T>
			totalRows={totalFilteredRows || totalRows}
			data={filteredData}
			columns={columns}
			isLoading={isLoading}
			isBarLoader={isLoading}
			columnVisibility={columnVisibility || tableStore.getColumnVisibility()}
			defaultSortBy={defaultSortBy}
			debugProps={debugProps}
			isSummary={isSummary}
			makeLinerPortable={makeLinerPortable}
			teleportRootId={teleportRootId}
			globalFilter={tableStore.getFilterTerm()}
			setGlobalFilter={(val: string) => tableStore.setFilterTerm(val)}
			onPageChange={(paginatonProps: PaginationState) => {
				tooltipsStore.removeTooltips();
				const { pageIndex } = paginatonProps;
				tableStore.setPageIndex(pageIndex);
				if (onPageChange) {
					onPageChange(paginatonProps);
				}
			}}
			onPageSize={({ pageSize }: PaginationState) => tableStore.setPageSize(pageSize)}
			pagination={{
				pageIndex: tableStore.getPageIndex(),
				pageSize: tableStore.getPageSize(),
			}}
			{...performanceProps}
			isActionLiner={isActionLiner}
			currentRoute={currentRoute}
			creativesLinkedToggle={creativesLinkedToggle}
			isEmptyAndDisabled={isEmptyAndDisabled}
			isGlobalFilter={isGlobalFilter}
			removeColumnsPicker={removeColumnsPicker}
			isFiltersActivated={!!currentFilters?.size || !!filterTerm?.length}
			onSorting={newSorting => {
				const current = tableStore.getSortingBy();
				const changed =
					newSorting.length !== current.length ||
					newSorting.some((s, i) => s.id !== current[i]?.id || s.desc !== current[i]?.desc);

				if (changed) {
					tableStore.setSortingBy([...newSorting]); // clone to trigger MobX
				}
			}}
			{...rest}>
			{(props: { rows: Row<T>[]; sortedRows: Row<T>[] }) => {
				const { rows, sortedRows } = props;

				if (children) {
					TableActions = children({
						rows,
						sortedRows,
					});
				}
				const DefaultIndexActions = () => {
					if (statsFilters) {
						return <React.Fragment>{statsFilters}</React.Fragment>;
					}

					if (!createRoute) {
						return null;
					}

					return (
						<PrimaryLink
							icon={'plus'}
							to={`${createRoute}`}
							width={'100px'}
							state={{ isBlockRequest: true } as BetweenPagesStateProps}
							debugProps={{ dataAttrs: suffixToDataAttr('_create', dataAttrs) }}>
							Create
						</PrimaryLink>
					);
				};

				return {
					IndexActions: (() => {
						if (TableActions.IndexActions) {
							return TableActions.IndexActions;
						}

						if (statsFilters && !createRoute) {
							return () => <React.Fragment>{statsFilters}</React.Fragment>;
						}

						if (!createRoute && !statsFilters) {
							return null;
						}

						return DefaultIndexActions;
					})(),
					...(statsFilters || hideFiltersActions ? { ...TableActions, FiltersActions: undefined } : TableActions),
				};
			}}
		</Table>
	);
});

export const ControlledTable = memo(_ControlledTable) as typeof _ControlledTable;
